//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import React from 'react'
import { Box, Typography, IconButton, useTheme } from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'

interface VaultAccessItemProps {
  poolAddr: string
  poolName: string
  onCopyAddress: (address: string) => void
  showCopyButton?: boolean
}

export default function VaultAccessItem({
  poolAddr,
  poolName,
  onCopyAddress,
  showCopyButton = true,
}: VaultAccessItemProps): React.ReactElement {
  const theme = useTheme()

  // Shorten the address: first 6 characters + "..." + last 4 characters
  const shortenAddress = (address: string): string => {
    if (address.length <= 10) return address
    return `${address.slice(0, 6)}...${address.slice(-4)}`
  }

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        width: '100%',
        p: 1.5,
        borderRadius: 1,
        border: `1px solid ${theme.palette.grey[200]}`,
        backgroundColor: theme.palette.grey[50],
      }}
    >
      <Box sx={{ flex: 1, width: '90%' }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 'bold',
            mb: 0.25,
            fontSize: '0.875rem',
          }}
        >
          {poolName}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.grey[600],
            fontFamily: 'monospace',
            fontSize: '0.75rem',
          }}
        >
          {shortenAddress(poolAddr)}
        </Typography>
      </Box>
      {showCopyButton && (
        <IconButton
          size="small"
          onClick={() => onCopyAddress(poolAddr)}
          sx={{
            color: theme.palette.grey[600],
            ml: 1,
          }}
        >
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  )
}
